import { canDoProjectAction } from '@projektverwaltung/shared';
import type {
  IntakeHandoverFields,
  LifecycleStatus,
  ProjectReminder,
  Zusammenfuehrung,
} from '@projektverwaltung/shared';
/**
 * V1.5 Projekt-Detail.
 * Quelle: Implementation Brief §7.5 + Screen-Inventory §6.
 *
 * Aufbau:
 *   - Lifecycle-Banner (wenn completed)
 *   - Header (Mono-Key + Customer + Sub-Row + Action-Buttons)
 *   - Phase-Trail (6 Steps mit current/done/pending/blocked)
 *   - 2-Spalten-Grid: Tabs (links) + Right-Rail (rechts, 320px)
 *
 * Tabs: Übersicht / Aufträge & CRs / Übergaben / Aufgaben / Kommentare /
 *       Briefings / Audit
 *
 * Diese Iteration ist read-only und konzentriert sich auf Layout + Daten-
 * Anzeige. Move-Phase-Modal, Inline-Editor, QS/Block-Aktionen kommen in
 * Phase 5.11–5.14 als Folge-Subschritte.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MentionInput, renderMentionBody } from '../../components/mention-input.js';
import { useToast } from '../../components/toast.js';
import { AddAuftragModal } from '../../components/v2/add-auftrag-modal.js';
import { AssignableRow } from '../../components/v2/assignable-row.js';
import { AttachmentList } from '../../components/v2/attachment-list.js';
import { Badge } from '../../components/v2/badge.js';
import { BEFUNDE_QUERY_KEY, useBefundLocale } from '../../components/v2/befunde/befund-basis.js';
import { BriefingView } from '../../components/v2/briefing-view.js';
import { Card } from '../../components/v2/card.js';
import { useConfirm } from '../../components/v2/confirm-dialog.js';
import { DueDateEditor } from '../../components/v2/due-date-editor.js';
import { FinalAktion, FinalBadge } from '../../components/v2/final-kennzeichen.js';
import {
  HandoverForm,
  OUTBOUND_FIELDS,
  type OutboundFieldKey,
} from '../../components/v2/handover-form.js';
import { HerkunftBadge } from '../../components/v2/herkunft-badge.js';
import { IntakeNotesBlock } from '../../components/v2/intake-notes-block.js';
import { KlFollowupDateEditor } from '../../components/v2/kl-followup-date-editor.js';
import { McpSessionWarning } from '../../components/v2/mcp-session-warning.js';
import { MovePhaseModal } from '../../components/v2/move-phase-modal.js';
import { type ActionKind, ProjectActionModal } from '../../components/v2/project-action-modal.js';
import { ReminderBar } from '../../components/v2/reminder-bar.js';
import { ResponsibilityPicker } from '../../components/v2/responsibility-picker.js';
import { SfFileImportModal } from '../../components/v2/sf-file-import-modal.js';
import { SFInfoCard } from '../../components/v2/sf-info-card.js';
import { Spinner } from '../../components/v2/spinner.js';
import { StaffingComments } from '../../components/v2/staffing-comments.js';
import { StaffingMaske } from '../../components/v2/staffing-maske/staffing-maske.js';
import { TagPicker } from '../../components/v2/tag-picker.js';
import { UebergabeNotiz } from '../../components/v2/uebergabe-notiz.js';
import {
  ZusammenfuehrungBanner,
  ZusammengefuehrtAusHinweis,
} from '../../components/v2/zusammenfuehrung-banner.js';
import { api } from '../../lib/api.js';
import { useFeatureFlag } from '../../lib/features.js';
import { entwurfSchluessel } from '../../lib/kommentar-entwurf.js';
import {
  useCanAssignLeadManager,
  useCanSyncProject,
  useStaffingPermissions,
} from '../../lib/permissions.js';
import { NurLesbarContext, useNurLesbar, useReaderMode } from '../../lib/reader.js';
import { useSession } from '../../lib/session.js';
import { sfAuftragZeile } from '../../lib/sf-auftrag.js';
import { sfLightningBaseUrl } from '../../lib/sf-lightning.js';
import { verlaufNotiz } from '../../lib/verlauf-notiz.js';
import { nichtZugeordnetAnzahl, offenAnzahl } from './staffing-neu.js';

type Project = {
  project_id: string;
  project_key: string;
  name: string;
  customer_name: string;
  segment: string;
  phase_id: string;
  phase_changed_at: string;
  lifecycle_status: LifecycleStatus;
  completed_at?: string | null;
  completion_reason?: string | null;
  staffing_needed: boolean;
  staffing_completed_at?: string | null;
  staffing_completed_by?: string | null;
  is_blocked?: boolean;
  blocked_reason?: string | null;
  qs_with_bl_open?: boolean;
  responsibility?: 'PU' | 'Service' | 'MTB' | null;
  ms_classification?: 'msPeople' | 'msSolution' | 'msService' | null;
  project_lead_user_id?: string | null;
  manager_user_id?: string | null;
  mtb_user_id?: string | null;
  last_sync_at?: string | null;
  salesforce_url?: string | null;
  due_date?: string | null;
  salesforce_data?: {
    projektstatus?: string | null;
    sparte?: string | null;
    abrechnungsart?: string | null;
    budget?: number | null;
    projektnotizen?: string | null;
    opportunity?: { id: string; name: string } | null;
    qm_kontakt?: { id: string; name: string } | null;
    endkunde?: { id: string; name: string } | null;
  } | null;
  handover_ready_fields?: Record<string, unknown> | null;
  intake_handover_fields?: Record<string, unknown> | null;
  /** Abgeleitete Erinnerung „was ist hier zu tun" (Spec 2026-09-16, §3.7), aus Task 9. */
  reminder?: ProjectReminder | null;
  /** Eingangsphase (#78) — traegt den Zuteilungsschutz. Quelle: `phases.is_intake`. */
  phase_is_intake?: boolean;
  /** Uebergabephase (#93) — dort ist die Abschlussbegruendung optional. Quelle: `phases.is_handover`. */
  phase_is_handover?: boolean;
  /** PV-052 L2 (A7/A11): „final geschlossen" — automatisch, von Hand oder Altbestand, Task 14. */
  final_geschlossen_am?: string | null;
  final_geschlossen_von?: string | null;
  final_geschlossen_grund?: string | null;
  final_altbestand?: boolean;
  /** Ausstehende Uebertragungen nach Salesforce (#78). Leer/undefined = alles zugestellt. */
  sf_write_pending?: Array<{
    topic: string;
    label: string;
    since: string;
    attempts: number;
    visible: boolean;
    error_kind?: 'retryable' | 'permanent' | null;
    error_message?: string | null;
    last_attempt_at?: string | null;
  }>;
  /** #123: Banner (zusammengeführte Quelle) und Hinweis „zusammengeführt aus …" (Ziel). */
  zusammenfuehrung?: Zusammenfuehrung;
};

type Auftrag = {
  auftrag_id: string;
  project_key: string;
  name: string;
  customer_name: string;
  kind: 'auftrag' | 'cr';
  ms_classification?: string | null;
  responsibility?: string | null;
  sf_budget_eur?: string | null;
  sf_projektstatus?: string | null;
  sf_projektstart?: string | null;
  sf_projektende?: string | null;
  individual_lifecycle_status?: string | null;
  // #131: Zeitpunkt + Grund des individuellen Abschlusses (bei `sf_deleted` automatisch gesetzt).
  individual_completed_at?: string | null;
  individual_completion_reason?: string | null;
  segment?: string | null;
};

/**
 * Status-Badge eines Auftrags im Reiter „Aufträge & CRs". #131: `sf_deleted` = das SF-Gegenstück
 * liegt im Papierkorb, die PV hat den Auftrag automatisch abgeschlossen. Sichtbar unabhängig vom
 * Befund-Flag (auf Prod aus) — sonst sähe niemand, warum der Auftrag/das Projekt zu ist.
 */
function AuftragStatusBadge({ auftrag }: { auftrag: Auftrag }) {
  const { t, i18n } = useTranslation();
  if (auftrag.individual_lifecycle_status === 'completed_early') {
    return (
      <Badge tone="success">
        {t('v2.detail.auftraege_individually_completed', 'individuell abgeschl.')}
      </Badge>
    );
  }
  if (auftrag.individual_lifecycle_status === 'sf_deleted') {
    const am = auftrag.individual_completed_at
      ? new Intl.DateTimeFormat(i18n.language, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(auftrag.individual_completed_at))
      : null;
    return (
      <Badge
        tone="danger"
        title={t(
          'v2.detail.auftraege_sf_deleted_title',
          'Der Auftrag wurde in Salesforce gelöscht; die PV hat ihn automatisch abgeschlossen.',
        )}
      >
        {am
          ? t('v2.detail.auftraege_sf_deleted_am', 'in Salesforce gelöscht · {{datum}}', {
              datum: am,
            })
          : t('v2.detail.auftraege_sf_deleted', 'in Salesforce gelöscht')}
      </Badge>
    );
  }
  return null;
}

type Tab =
  | 'overview'
  | 'auftraege'
  | 'handover'
  | 'tasks'
  | 'staffing'
  | 'comments'
  | 'briefings'
  | 'audit';

export function ProjectDetailV2Page() {
  const { projectId = '' } = useParams<{ projectId: string }>();
  // Deeplink ?tab= (z. B. aus der Tagging-Mail → direkt in den Kommentar-Tab).
  const [searchParams] = useSearchParams();
  return <ProjektDetail projectId={projectId} startTab={searchParams.get('tab')} />;
}

/**
 * Inhalt des Projektdetails (PV-052 L5, #108). Route-Seite und Projekt-Popup
 * (`components/v2/befunde/projekt-popup.tsx`) rendern dieselbe Komponente. `eingebettet`
 * blendet den Zurück-Link aus — im Popup schließt dessen Kopf. `BackLink` ist der einzige
 * `navigate`-Aufruf der Seite.
 */
export function ProjektDetail({
  projectId,
  eingebettet = false,
  startTab = null,
}: {
  projectId: string;
  eingebettet?: boolean;
  startTab?: string | null;
}) {
  const { t } = useTranslation();
  const rollenLeser = useReaderMode();
  const showTasksTab = useFeatureFlag('project_detail_tasks_tab');
  const showBriefingsTab = useFeatureFlag('mission_briefings_in_project_detail');
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const requested = startTab;
    const allowed: Tab[] = [
      'overview',
      'auftraege',
      'handover',
      'tasks',
      'staffing',
      'comments',
      'briefings',
      'audit',
    ];
    return requested && (allowed as string[]).includes(requested) ? (requested as Tab) : 'overview';
  });
  const [movePhaseOpen, setMovePhaseOpen] = useState(false);
  const [actionKind, setActionKind] = useState<ActionKind | null>(null);
  // Deeplink-Schutz: gegatete Tabs fallen auf Übersicht zurück.
  const effectiveTab: Tab =
    (activeTab === 'tasks' && !showTasksTab) || (activeTab === 'briefings' && !showBriefingsTab)
      ? 'overview'
      : activeTab;

  const projectQuery = useQuery({
    queryKey: ['v2-project', projectId],
    queryFn: () => api.getProject(projectId) as Promise<Project>,
    enabled: !!projectId,
  });
  const phasesQuery = useQuery({ queryKey: ['v2-phases'], queryFn: () => api.listPhases() });
  const auftraegeQuery = useQuery({
    queryKey: ['v2-project-auftraege', projectId],
    queryFn: () =>
      api.listAuftraegeForProject(projectId) as Promise<{
        items: Auftrag[];
        leading_auftrag_id: string;
      }>,
    enabled: !!projectId,
  });

  // #163 (Spec 4.3): Zählmarke „zu bestätigen" am Staffing-Reiter. Gleicher Query-Schlüssel wie
  // die Maske im Reiter — React Query teilt den Abruf, kein eigener Endpunkt.
  const staffingMaskeQuery = useQuery({
    queryKey: ['staffing-maske', projectId],
    queryFn: () => api.getStaffingMaske(projectId),
    enabled: !!projectId,
  });

  // PV-052: Befunde des Projekts für die Hinweisleiste — nur mit Flag `befunde`.
  const befundeAn = useFeatureFlag('befunde');
  const befundLocale = useBefundLocale();
  const befundeQuery = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'projekt', projectId, befundLocale],
    queryFn: () => api.listBefundeProjekt(projectId, befundLocale),
    enabled: befundeAn && !!projectId,
  });

  // i18n via window i18n (shorthand). Bei späterer Refactor-Phase über useTranslation().
  if (projectQuery.isLoading)
    return (
      <div className="p-8 text-sm" style={{ color: 'var(--ink-3)' }}>
        {t('v2.detail.loading', 'Lädt…')}
      </div>
    );
  if (!projectQuery.data)
    return (
      <div className="p-8 text-sm" style={{ color: 'var(--ink-3)' }}>
        {t('v2.detail.not_found', 'Projekt nicht gefunden.')}
      </div>
    );

  const project = projectQuery.data;
  // #123: Ein zusammengeführtes Projekt ist für jeden nur lesbar. Der Kontext unten trägt das in
  // alle Unterkomponenten (`useReaderMode()`); diese Komponente selbst liegt außerhalb davon.
  const nurLesbar = project.lifecycle_status === 'merged';
  const isReader = rollenLeser || nurLesbar;
  const phases = (phasesQuery.data?.items ?? []).slice().sort((a, b) => a.order - b.order);
  const auftraege = auftraegeQuery.data?.items ?? [];
  const leadingId = auftraegeQuery.data?.leading_auftrag_id;

  // #93: Der Abschluss ist aus JEDER Phase moeglich. Die frueher hier stehende Bedingung
  // „nur aus Neu oder Handover" war eine reine Sichtbarkeitsregel im Frontend — die Route
  // kannte sie nie. An ihre Stelle tritt die Begruendungspflicht ausserhalb von Handover,
  // die im Service sitzt (`services/project-lifecycle.ts`) und damit auch fuer REST und MCP gilt.
  const canComplete = project.lifecycle_status === 'active';
  // Phasenzugehoerigkeit kommt aus `phases.is_handover`, nicht aus einem Namensvergleich —
  // ein namensbasierter Riegel waere durch Umbenennen still aushebelbar.
  const completeReasonMode: 'required' | 'optional' = project.phase_is_handover
    ? 'optional'
    : 'required';

  return (
    <NurLesbarContext.Provider value={nurLesbar}>
      <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-5">
        {project.lifecycle_status === 'completed' && (
          <CompletedBanner project={project} onReactivate={() => setActionKind('reactivate')} />
        )}
        {nurLesbar && project.zusammenfuehrung && (
          <ZusammenfuehrungBanner zusammenfuehrung={project.zusammenfuehrung} />
        )}
        {project.zusammenfuehrung && project.zusammenfuehrung.aus.length > 0 && (
          <ZusammengefuehrtAusHinweis aus={project.zusammenfuehrung.aus} />
        )}

        {!eingebettet && <BackLink />}

        <Header
          project={project}
          onMovePhase={() => setMovePhaseOpen(true)}
          onAction={setActionKind}
          canMovePhase={project.lifecycle_status === 'active' && !isReader && !project.is_blocked}
          canComplete={canComplete && !isReader}
          isReader={isReader}
        />

        <ReminderBar
          reminder={project.reminder}
          canEdit={!isReader && project.lifecycle_status === 'active'}
          onMovePhase={() => setMovePhaseOpen(true)}
          onUnblock={() => setActionKind('unblock')}
          onEditDueDate={() => {
            setActiveTab('overview');
            // Nach dem Tab-Wechsel steht das Feld erst im naechsten Tick im DOM. Der Kalender
            // oeffnet sich direkt (showPicker, Chromium/Firefox/Safari 16+); wo der Browser das
            // nicht erlaubt (aeltere Engines, fehlende Nutzeraktivierung), bleibt der Fokus.
            window.setTimeout(() => {
              const input = document.getElementById('due-date-editor')?.querySelector('input');
              if (!input) return;
              input.focus();
              try {
                input.showPicker?.();
              } catch {
                // NotAllowedError o. ae. — Fokus reicht dann.
              }
            }, 0);
          }}
          onOpenHandover={() => setActiveTab('handover')}
          befunde={befundeAn ? befundeQuery.data?.items : undefined}
          befundeFehler={befundeAn && befundeQuery.isError}
        />

        <PhaseTrail
          phases={phases}
          currentPhaseId={project.phase_id}
          blocked={project.is_blocked}
        />

        <MovePhaseModal
          open={movePhaseOpen}
          onClose={() => setMovePhaseOpen(false)}
          project={project}
          phases={phases}
          gate={phasesQuery.data?.gate}
        />
        <ProjectActionModal
          kind={actionKind}
          projectId={project.project_id}
          onClose={() => setActionKind(null)}
          completeReason={completeReasonMode}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div>
            <TabsBar
              activeTab={effectiveTab}
              onTab={setActiveTab}
              counts={{
                auftraege: auftraege.length,
                zuBestaetigen: staffingMaskeQuery.data?.zu_bestaetigen ?? 0,
              }}
              showTasks={showTasksTab}
              showBriefings={showBriefingsTab}
            />
            <div className="mt-4">
              {effectiveTab === 'overview' && <OverviewTab project={project} />}
              {effectiveTab === 'auftraege' && (
                <AuftraegeTab
                  auftraege={auftraege}
                  leadingId={leadingId}
                  projectId={project.project_id}
                />
              )}
              {effectiveTab === 'handover' && <HandoverTab project={project} phases={phases} />}
              {effectiveTab === 'tasks' && showTasksTab && (
                <TasksTab projectId={project.project_id} />
              )}
              {effectiveTab === 'staffing' && <StaffingTab project={project} />}
              {effectiveTab === 'comments' && <CommentsTab projectId={project.project_id} />}
              {effectiveTab === 'briefings' && showBriefingsTab && (
                <BriefingsTab project={project} />
              )}
              {effectiveTab === 'audit' && <AuditTab projectId={project.project_id} />}
            </div>
          </div>
          <RightRail project={project} />
        </div>
      </div>
    </NurLesbarContext.Provider>
  );
}

// ===========================================================================
// Lifecycle-Banner (Completed)
// ===========================================================================

function CompletedBanner({
  project,
  onReactivate,
}: { project: Project; onReactivate: () => void }) {
  const { t, i18n } = useTranslation();
  const isReader = useReaderMode();
  const completedDate = project.completed_at
    ? new Intl.DateTimeFormat(i18n.language).format(new Date(project.completed_at))
    : '—';
  return (
    <div
      className="rounded-lg p-4 flex items-start gap-3"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
    >
      <div className="w-1 self-stretch rounded-full" style={{ background: 'var(--success)' }} />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="text-[10.5px] uppercase tracking-[0.14em]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--success)' }}
          >
            {t('v2.detail.completed_eyebrow', 'Abgeschlossen')}
          </div>
          <FinalBadge project={project} />
        </div>
        <div className="text-sm" style={{ color: 'var(--ink)' }}>
          {completedDate}
          {project.completion_reason ? (
            <>
              {' '}
              · <em style={{ color: 'var(--ink-2)' }}>{project.completion_reason}</em>
            </>
          ) : null}
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>
          {t('v2.detail.completed_locked', 'Bearbeitung gesperrt.')}{' '}
          {t('v2.detail.completed_locked_hint', 'Reaktivieren über das BL/Manager-Menü.')}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <FinalAktion project={project} />
        <button
          type="button"
          onClick={onReactivate}
          disabled={isReader}
          title={isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined}
          className="text-xs px-3 py-1.5 rounded-md shrink-0"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: isReader ? 0.5 : 1,
            cursor: isReader ? 'not-allowed' : 'pointer',
          }}
        >
          {t('v2.detail.btn_reactivate', 'Reaktivieren')}
        </button>
      </div>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasHistory = typeof window !== 'undefined' && window.history.length > 1;
  return (
    <button
      type="button"
      onClick={() => (hasHistory ? navigate(-1) : navigate('/'))}
      className="inline-flex items-center gap-1.5 text-xs"
      style={{
        color: 'var(--ink-3)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--f-mono)',
        padding: 0,
      }}
    >
      {t('v2.detail.back', '← Zurück')}
    </button>
  );
}

// ===========================================================================
// Header
// ===========================================================================

function Header({
  project,
  onMovePhase,
  onAction,
  canMovePhase,
  canComplete,
  isReader,
}: {
  project: Project;
  onMovePhase: () => void;
  onAction: (kind: ActionKind) => void;
  canMovePhase: boolean;
  canComplete: boolean;
  isReader: boolean;
}) {
  const { t } = useTranslation();
  const readerTooltip = t('v2.reader.tooltip', 'Sie haben Leserechte');
  const flags: Array<{ tone: 'danger' | 'warn' | 'primary'; label: string }> = [];
  if (project.is_blocked) {
    flags.push({ tone: 'danger', label: t('v2.detail.flag_blocked', 'blockiert').toUpperCase() });
  }
  if (project.qs_with_bl_open) flags.push({ tone: 'primary', label: 'QS MIT BL' });
  if (project.staffing_needed) flags.push({ tone: 'warn', label: 'STAFFING' });

  return (
    <header className="space-y-3 pb-4 border-b" style={{ borderColor: 'var(--line-soft)' }}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <span
            style={{
              fontFamily: 'var(--f-mono)',
              color: 'var(--primary)',
              letterSpacing: '0.06em',
              fontSize: 13,
            }}
          >
            {project.project_key}
          </span>
          <h1
            className="font-medium leading-[1.05] tracking-tight mt-1"
            style={{
              fontFamily: 'var(--f-display)',
              color: 'var(--ink)',
              fontSize: 'clamp(22px, 2.4vw, 36px)',
            }}
          >
            <em className="not-italic" style={{ fontStyle: 'italic' }}>
              {project.customer_name}
            </em>
          </h1>
          <div className="text-base mt-1" style={{ color: 'var(--ink-3)' }}>
            {project.name}
          </div>
          <div
            className="flex items-center gap-2 mt-3 flex-wrap text-[11px]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            <span>{project.segment}</span>
            <span>·</span>
            <span>
              {t('v2.bereich.label', 'Bereich')}:{' '}
              {project.responsibility ?? t('v2.bereich.open', 'offen')}
            </span>
            {project.ms_classification && (
              <>
                <span>·</span>
                <span>{project.ms_classification}</span>
              </>
            )}
            {flags.map((f) => (
              <span
                key={f.label}
                className="ml-1 px-1.5 py-0.5 rounded"
                style={{
                  background:
                    f.tone === 'danger'
                      ? 'var(--danger-soft)'
                      : f.tone === 'warn'
                        ? 'var(--warn-soft)'
                        : 'var(--primary-soft)',
                  color:
                    f.tone === 'danger'
                      ? 'var(--danger)'
                      : f.tone === 'warn'
                        ? 'var(--warn)'
                        : 'var(--primary)',
                }}
              >
                {f.label}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap justify-end max-w-[60%]">
          {project.lifecycle_status === 'active' && (
            <>
              {project.is_blocked ? (
                <ActionBtn
                  onClick={() => onAction('unblock')}
                  disabled={isReader}
                  title={isReader ? readerTooltip : undefined}
                >
                  {t('v2.detail.btn_block_resolve', 'Block lösen')}
                </ActionBtn>
              ) : (
                <ActionBtn
                  onClick={() => onAction('block')}
                  disabled={isReader}
                  title={isReader ? readerTooltip : undefined}
                >
                  {t('v2.detail.btn_block_set', 'Block setzen')}
                </ActionBtn>
              )}
              {project.qs_with_bl_open ? (
                <ActionBtn
                  onClick={() => onAction('qs-resolve')}
                  disabled={isReader}
                  title={isReader ? readerTooltip : undefined}
                >
                  {t('v2.detail.btn_qs_resolve', 'QS-BL lösen')}
                </ActionBtn>
              ) : (
                <ActionBtn
                  onClick={() => onAction('qs-flag')}
                  disabled={isReader}
                  title={isReader ? readerTooltip : undefined}
                >
                  {t('v2.detail.btn_qs_flag', 'QS mit BL')}
                </ActionBtn>
              )}
              {canComplete && (
                <ActionBtn
                  onClick={() => onAction('complete')}
                  disabled={isReader}
                  title={isReader ? readerTooltip : undefined}
                >
                  {t('v2.detail.btn_complete', 'Abschließen…')}
                </ActionBtn>
              )}
            </>
          )}
          <ActionBtn
            primary
            onClick={canMovePhase ? onMovePhase : undefined}
            disabled={!canMovePhase}
            title={
              isReader
                ? readerTooltip
                : project.is_blocked && project.lifecycle_status === 'active'
                  ? t('v2.detail.move_phase_blocked_tooltip', 'Erst Block lösen')
                  : undefined
            }
          >
            {t('v2.detail.btn_move_phase', 'Phase wechseln →')}
          </ActionBtn>
        </div>
      </div>
    </header>
  );
}

function ActionBtn({
  children,
  primary = false,
  onClick,
  disabled = false,
  title,
}: {
  children: React.ReactNode;
  primary?: boolean;
  onClick?: (() => void) | undefined;
  disabled?: boolean;
  title?: string | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="text-xs px-3 py-1.5 rounded-md transition-colors"
      style={{
        background: primary ? 'var(--primary)' : 'var(--surface-2)',
        color: primary ? 'white' : 'var(--ink-2)',
        border: primary ? 'none' : '1px solid var(--line-soft)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// ===========================================================================
// Phase Trail
// ===========================================================================

function PhaseTrail({
  phases,
  currentPhaseId,
  blocked,
}: {
  phases: Array<{ phase_id: string; name: string; order: number }>;
  currentPhaseId: string;
  blocked?: boolean | undefined;
}) {
  const currentIdx = phases.findIndex((p) => p.phase_id === currentPhaseId);

  return (
    <div className="flex items-center gap-1 px-1 overflow-x-auto">
      {phases.map((ph, i) => {
        const isDone = i < currentIdx;
        const isCurrent = i === currentIdx;
        const baseColor = isCurrent
          ? blocked
            ? 'var(--warn)'
            : 'var(--primary)'
          : isDone
            ? 'var(--primary)'
            : 'var(--ink-4)';
        return (
          <div key={ph.phase_id} className="flex items-center gap-1 flex-1 min-w-0">
            <div className="flex flex-col items-center gap-1">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-medium transition-colors"
                style={{
                  background: isCurrent
                    ? baseColor
                    : isDone
                      ? 'var(--primary-soft)'
                      : 'transparent',
                  color: isCurrent ? 'var(--bg-deeper)' : baseColor,
                  border: `2px solid ${baseColor}`,
                  fontFamily: 'var(--f-mono)',
                  boxShadow: isCurrent
                    ? `0 0 0 4px ${blocked ? 'var(--warn-soft)' : 'var(--primary-soft)'}`
                    : 'none',
                }}
              >
                {String(ph.order).padStart(2, '0')}
              </div>
              <span
                className="text-[10px] uppercase tracking-[0.1em] whitespace-nowrap"
                style={{
                  fontFamily: 'var(--f-mono)',
                  color: isCurrent ? 'var(--ink)' : 'var(--ink-3)',
                  fontWeight: isCurrent ? 600 : 400,
                }}
              >
                {ph.name}
              </span>
            </div>
            {i < phases.length - 1 && (
              <div
                className="flex-1 h-px"
                style={{ background: i < currentIdx ? 'var(--primary)' : 'var(--line-soft)' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Tabs
// ===========================================================================

function TabsBar({
  activeTab,
  onTab,
  counts,
  showTasks,
  showBriefings,
}: {
  activeTab: Tab;
  onTab: (t: Tab) => void;
  counts: { auftraege: number; zuBestaetigen: number };
  showTasks: boolean;
  showBriefings: boolean;
}) {
  const { t } = useTranslation();
  const tabs: Array<{ id: Tab; label: string; count?: number; warn?: string }> = [
    { id: 'overview', label: t('v2.detail.tab_overview', 'Übersicht') },
    {
      id: 'auftraege',
      label: t('v2.detail.tab_auftraege', 'Aufträge & CRs'),
      count: counts.auftraege,
    },
    { id: 'handover', label: t('v2.detail.tab_handover', 'Übergaben') },
    ...(showTasks ? [{ id: 'tasks' as Tab, label: t('v2.detail.tab_tasks', 'Aufgaben') }] : []),
    {
      id: 'staffing',
      label: t('v2.detail.tab_staffing', 'Staffing'),
      // #163: Zählmarke nur, solange KI-Vorschläge offen sind — im Warnton.
      ...(counts.zuBestaetigen > 0
        ? {
            count: counts.zuBestaetigen,
            warn: t('v2.staffing_maske.zu_bestaetigen_anzahl', { n: counts.zuBestaetigen }),
          }
        : {}),
    },
    { id: 'comments', label: t('v2.detail.tab_comments', 'Kommentare') },
    ...(showBriefings
      ? [{ id: 'briefings' as Tab, label: t('v2.detail.tab_briefings', 'Briefings') }]
      : []),
    { id: 'audit', label: t('v2.detail.tab_audit', 'Audit') },
  ];
  return (
    <nav className="flex gap-1 border-b" style={{ borderColor: 'var(--line-soft)' }}>
      {tabs.map((t) => {
        const active = activeTab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onTab(t.id)}
            title={t.warn}
            className="px-3 py-2 text-sm transition-colors relative"
            style={{
              color: active ? 'var(--ink)' : 'var(--ink-3)',
              fontWeight: active ? 500 : 400,
            }}
          >
            {t.label}
            {t.count !== undefined && (
              <span
                className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded"
                style={
                  t.warn
                    ? {
                        background: 'var(--staffing-zu_bestaetigen)',
                        color: 'var(--staffing-zu_bestaetigen-ink)',
                        fontFamily: 'var(--f-mono)',
                        fontWeight: 600,
                      }
                    : {
                        background: 'var(--surface-2)',
                        color: 'var(--ink-3)',
                        fontFamily: 'var(--f-mono)',
                      }
                }
              >
                {t.count}
              </span>
            )}
            {active && (
              <span
                className="absolute -bottom-px left-0 right-0 h-0.5"
                style={{ background: 'var(--primary)' }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ===========================================================================
// Tab: Overview
// ===========================================================================

function OverviewTab({ project }: { project: Project }) {
  const { t, i18n } = useTranslation();
  const isReader = useReaderMode();
  const canEdit = !isReader && project.lifecycle_status === 'active';
  const startedDate = new Intl.DateTimeFormat(i18n.language).format(
    new Date(project.phase_changed_at),
  );
  return (
    <div className="space-y-4">
      <Card>
        <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--ink)' }}>
          {t('v2.detail.ov_section', 'Schnellfakten')}
        </h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Field
            label={t('v2.detail.ov_project_key', 'Project-Key')}
            value={<span style={{ fontFamily: 'var(--f-mono)' }}>{project.project_key}</span>}
          />
          <Field label={t('v2.detail.ov_customer', 'Kunde')} value={project.customer_name} />
          <Field label={t('v2.detail.ov_segment', 'Segment')} value={project.segment} />
          <Field
            label={t('v2.detail.ov_bereich', 'Bereich')}
            value={
              <ResponsibilityPicker
                projectId={project.project_id}
                value={project.responsibility ?? null}
                canEdit={canEdit}
              />
            }
          />
          <Field
            label={t('v2.detail.ov_ms_class', 'Dienstleistungsart')}
            value={project.ms_classification ?? '—'}
          />
          <Field
            label={t('v2.detail.ov_last_phase_change', 'Letzte Phase-Änderung')}
            value={startedDate}
          />
          <Field
            label={t('v2.detail.ov_lifecycle', 'Lifecycle')}
            value={
              project.lifecycle_status === 'completed'
                ? t('v2.detail.ov_lifecycle_completed', 'abgeschlossen')
                : project.lifecycle_status === 'merged'
                  ? t('v2.detail.ov_lifecycle_merged', 'zusammengeführt')
                  : t('v2.detail.ov_lifecycle_active', 'aktiv')
            }
          />
          <Field
            label={t('v2.duedate.label', 'Fälligkeit')}
            value={
              <div id="due-date-editor">
                <DueDateEditor
                  projectId={project.project_id}
                  value={project.due_date ?? null}
                  canEdit={canEdit}
                />
              </div>
            }
          />
          <Field
            label={t('v2.detail.ov_flags', 'Flags')}
            value={
              <span className="flex gap-1.5 flex-wrap">
                {project.is_blocked && (
                  <Badge tone="danger">{t('v2.detail.flag_blocked', 'blockiert')}</Badge>
                )}
                {project.is_blocked && project.blocked_reason && (
                  <span
                    className="truncate max-w-[28ch]"
                    style={{ color: 'var(--ink-2)' }}
                    title={project.blocked_reason}
                  >
                    {project.blocked_reason}
                  </span>
                )}
                {project.qs_with_bl_open && <Badge tone="primary">qs-bl</Badge>}
                {project.staffing_needed && (
                  <Badge tone="warn">
                    {t('v2.detail.rail_staffing', 'Staffing').toLowerCase()}
                  </Badge>
                )}
                {!project.is_blocked && !project.qs_with_bl_open && !project.staffing_needed && (
                  <span style={{ color: 'var(--ink-3)' }}>{t('v2.detail.ov_no_flags', '—')}</span>
                )}
              </span>
            }
          />
        </dl>
      </Card>
      <SFInfoCard data={project.salesforce_data} salesforceUrl={project.salesforce_url} />
    </div>
  );
}

// ===========================================================================
// Tab: Aufträge & CRs
// ===========================================================================

function AuftraegeTab({
  auftraege,
  leadingId,
  projectId,
}: {
  auftraege: Auftrag[];
  leadingId?: string | undefined;
  projectId: string;
}) {
  const { t, i18n } = useTranslation();
  const isReader = useReaderMode();
  const toast = useToast();
  const confirmAction = useConfirm();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const invalidateAuftraege = () => {
    void queryClient.invalidateQueries({ queryKey: ['v2-project-auftraege', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    void queryClient.invalidateQueries({ queryKey: ['v2-auftraege-movable'] });
    // PV-052 L2: Der Server hat abgewartet neu geprüft — Befunde frisch laden.
    void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
  };

  const setLeadingMut = useMutation({
    mutationFn: (auftragId: string) => api.setLeadingAuftrag(projectId, auftragId),
    onSuccess: () => {
      invalidateAuftraege();
      toast.success(t('v2.detail.auftraege_lead_changed', 'Führender Auftrag aktualisiert.'));
    },
    onError: (err) =>
      toast.error(
        t('v2.detail.auftraege_lead_failed', 'Lead-Wechsel fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const detachMut = useMutation({
    mutationFn: (auftragId: string) => api.detachAuftrag(projectId, auftragId),
    onSuccess: (res) => {
      invalidateAuftraege();
      // #123: Hatte der Auftrag ein zusammengeführtes Ur-Projekt, lebt DAS wieder auf — kein
      // neues Solo-Projekt. Die Meldung muss das sagen und dorthin führen.
      if (res.wiederhergestellt && res.new_project) {
        const key = res.new_project.project_key;
        toast.push({
          kind: 'success',
          title: t('v2.zusammenfuehren.detach_wiederhergestellt', '{{key}} wiederhergestellt', {
            key,
          }),
          body: t(
            'v2.zusammenfuehren.detach_wiederhergestellt_body',
            'Das ursprüngliche Projekt ist wieder aktiv; seine Einträge sind zurückgekehrt.',
          ),
          link: {
            to: `/projects/${res.new_project.project_id}`,
            label: t('v2.zusammenfuehren.detach_zum_projekt', 'Zum Projekt {{key}}', { key }),
          },
        });
        return;
      }
      toast.success(
        t(
          'v2.detail.auftraege_detached',
          'Auftrag aus Projekt entfernt (in neues Solo-Projekt verschoben).',
        ),
      );
    },
    onError: (err) =>
      toast.error(
        t('v2.detail.auftraege_detach_failed', 'Entfernen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const canEdit = !isReader;
  const readerTooltip = isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined;

  if (auftraege.length === 0) {
    return (
      <Card>
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.detail.auftraege_empty', 'Keine Aufträge gefunden.')}
        </p>
      </Card>
    );
  }

  const canSplit = auftraege.length > 1;

  return (
    <>
      <Card padding={false}>
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'var(--line-soft)' }}
        >
          <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
            {t('v2.detail.auftraege_count', '{{count}} Auftrag/Aufträge zugeordnet', {
              count: auftraege.length,
            })}
          </h3>
          <ActionBtn
            onClick={canEdit ? () => setAddOpen(true) : undefined}
            disabled={!canEdit}
            title={readerTooltip}
          >
            {t('v2.detail.auftraege_add', '+ Auftrag hinzufügen')}
          </ActionBtn>
        </div>
        <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
          {auftraege.map((a) => {
            const isLeading = a.auftrag_id === leadingId;
            const sfZeile = sfAuftragZeile(a, i18n.language);
            return (
              <li
                key={a.auftrag_id}
                className="p-4 flex items-center gap-4 group"
                style={{ borderColor: 'var(--line-soft)' }}
              >
                {isLeading ? (
                  <span
                    title={t('v2.detail.auftraege_leading_title', 'Führender Auftrag')}
                    style={{ color: 'var(--primary)' }}
                  >
                    ★
                  </span>
                ) : canEdit ? (
                  <button
                    type="button"
                    onClick={() => setLeadingMut.mutate(a.auftrag_id)}
                    disabled={setLeadingMut.isPending}
                    title={t('v2.detail.auftraege_set_leading', 'Als führend setzen')}
                    className="opacity-30 hover:opacity-100 transition-opacity"
                    style={{
                      color: 'var(--ink-3)',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                    }}
                  >
                    ☆
                  </button>
                ) : (
                  <span style={{ color: 'var(--ink-4)' }}>☆</span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-2)' }}
                    >
                      {a.project_key}
                    </span>
                    <Badge tone={a.kind === 'cr' ? 'primary' : 'success'}>
                      {a.kind === 'cr'
                        ? t('v2.detail.auftraege_kind_cr', 'CR')
                        : t('v2.detail.auftraege_kind_auftrag', 'Auftrag')}
                    </Badge>
                    {a.ms_classification && <Badge tone="primary">{a.ms_classification}</Badge>}
                    <AuftragStatusBadge auftrag={a} />
                  </div>
                  <div className="text-sm" style={{ color: 'var(--ink)' }}>
                    {a.name}
                  </div>
                  {sfZeile && (
                    <div
                      className="text-[11px] mt-0.5"
                      style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
                    >
                      {sfZeile}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-2)' }}>
                    {a.sf_budget_eur ? `€${Number(a.sf_budget_eur).toLocaleString('de-DE')}` : '—'}
                  </div>
                  <div className="text-[10px]" style={{ color: 'var(--ink-3)' }}>
                    {a.responsibility ?? '—'}
                  </div>
                </div>
                {canEdit && canSplit && (
                  <button
                    type="button"
                    onClick={() => {
                      // #123: neutral — ob ein Ur-Projekt auflebt oder ein neues entsteht,
                      // entscheidet erst der Server.
                      const confirmMsg = isLeading
                        ? t(
                            'v2.zusammenfuehren.detach_confirm_lead',
                            'Auftrag {{key}} wird aus diesem Projekt herausgelöst. War er hierher zusammengeführt, lebt sein ursprüngliches Projekt wieder auf; sonst entsteht ein neues Projekt. Der nächstältere Auftrag wird automatisch zum führenden Auftrag.',
                            { key: a.project_key },
                          )
                        : t(
                            'v2.zusammenfuehren.detach_confirm',
                            'Auftrag {{key}} wird aus diesem Projekt herausgelöst. War er hierher zusammengeführt, lebt sein ursprüngliches Projekt wieder auf; sonst entsteht ein neues Projekt.',
                            { key: a.project_key },
                          );
                      void confirmAction({
                        title: t(
                          'v2.detail.auftraege_detach_confirm_title',
                          'Auftrag aus Projekt entfernen?',
                        ),
                        body: confirmMsg,
                        confirmLabel: t('v2.detail.auftraege_detach', 'Aus Projekt entfernen'),
                      }).then((ok) => {
                        if (ok) detachMut.mutate(a.auftrag_id);
                      });
                    }}
                    disabled={detachMut.isPending}
                    className="text-[11px] px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    style={{
                      background: 'var(--surface-2)',
                      color: 'var(--ink-2)',
                      border: '1px solid var(--line-soft)',
                    }}
                    title={t('v2.detail.auftraege_detach_title', 'Aus Projekt entfernen (Split)')}
                  >
                    {t('v2.detail.auftraege_detach', 'Aus Projekt entfernen')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
      <AddAuftragModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        targetProjectId={projectId}
      />
    </>
  );
}

// ===========================================================================
// Tab: Handover (Eingang + Ausgang)
// ===========================================================================

// Feldliste + Typ aus der geteilten Konstante abgeleitet (handover-form.tsx) — die Liste
// stand hier früher ein drittes Mal wortgleich.
type OutboundFields = Partial<Record<OutboundFieldKey, string>>;

type HandoverPhase = {
  phase_id: string;
  name: string;
  order: number;
  required_fields?: string[] | null | undefined;
  entry_required_fields?: string[] | null | undefined;
};

function HandoverTab({ project, phases }: { project: Project; phases: HandoverPhase[] }) {
  const { t } = useTranslation();
  const isReader = useReaderMode();
  const { user } = useSession();
  const canEditBase = !isReader && project.lifecycle_status === 'active';
  const queryClient = useQueryClient();
  // „Wieder fällig am" ist ein KL-Artefakt → Kaufm. Leitung (canResolve) ODER Scope-Manage.
  const { canManage: canManageStaffing, canResolve: canResolveStaffing } =
    useStaffingPermissions(project);
  const canFollowup = canResolveStaffing || canManageStaffing;
  const currentPhase = phases.find((p) => p.phase_id === project.phase_id);
  const outboundRequired = new Set(currentPhase?.required_fields ?? []);
  const invalidateProject = () => {
    void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
    // #119: eine Notiz kfm. Leitung (HandoverForm, Feld kfml_notiz) kann hier den
    // KFML-Worklist-Haken automatisch setzen — ohne diese Zeile bliebe die Worklist-Seite
    // stehen, bis irgendetwas anderes sie invalidiert oder neu geladen wird.
    void queryClient.invalidateQueries({ queryKey: ['v2-kfml-worklist'] });
  };

  // Übergabe-Felder folgen der echten Permission-Matrix (Issue #14): Intake = `edit_intake`
  // (u.a. MTB), Ausgang = `edit`. Reader/abgeschlossene Projekte bleiben außen vor.
  const scopeShape = {
    project_id: project.project_id,
    segment: project.segment as import('@projektverwaltung/shared').Segment,
    project_lead_user_id: project.project_lead_user_id ?? null,
    manager_user_id: project.manager_user_id ?? null,
    mtb_user_id: project.mtb_user_id ?? null,
    responsibility: project.responsibility ?? null,
    // #78: hier nur edit/edit_intake — der Zuteilungsschutz greift an der Verantwortlich-Leiste.
    phase_is_intake: project.phase_is_intake ?? false,
    lifecycle_status: project.lifecycle_status,
  };
  const canEditIntake =
    canEditBase && user != null && canDoProjectAction(user, scopeShape, 'edit_intake');
  const canEditOutbound =
    canEditBase && user != null && canDoProjectAction(user, scopeShape, 'edit');

  return (
    <div className="space-y-4">
      {/* FA-2: „Wieder fällig am" auch in der Übergabe-Ansicht setzbar (Staffing neu, Issue #1).
          Holt ein Projekt ohne Bedarf in die KL-Staffing-Liste. Keine Bedarfserfassung hier. */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.staffing.followup', 'Wieder fällig am')}
        </label>
        <KlFollowupDateEditor
          projectId={project.project_id}
          value={
            (project as unknown as { kl_followup_date?: string | null }).kl_followup_date ?? null
          }
          canEdit={canFollowup}
        />
      </div>
      <IntakeNotesBlock
        projectId={project.project_id}
        intakeFields={(project.intake_handover_fields ?? {}) as IntakeHandoverFields}
        canEdit={canEditIntake}
        isReader={isReader}
        onSaved={invalidateProject}
      />
      <HandoverForm
        kind="outbound"
        title={t('v2.detail.handover_outbound_title', 'Ausgangs-Übergabe')}
        hint={t('v2.detail.handover_outbound_hint', 'Pflicht beim Phasenwechsel auf Handover.')}
        fields={[...OUTBOUND_FIELDS]}
        required={outboundRequired}
        initial={(project.handover_ready_fields ?? {}) as OutboundFields}
        projectId={project.project_id}
        canEdit={canEditOutbound}
        isReader={isReader}
        onSaved={invalidateProject}
      />
    </div>
  );
}

// ===========================================================================
// Tab: Tasks
// ===========================================================================

function TasksTab({ projectId }: { projectId: string }) {
  const { t: tt, i18n } = useTranslation();
  const isReader = useReaderMode();
  const toast = useToast();
  const confirmAction = useConfirm();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['v2-tasks', projectId],
    queryFn: () => api.listTasks(projectId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['v2-tasks', projectId] });

  const createMut = useMutation({
    mutationFn: (input: { title: string; due_date: string | null }) =>
      api.createTask({ project_id: projectId, title: input.title, due_date: input.due_date }),
    onSuccess: () => {
      invalidate();
      toast.success(tt('v2.detail.tasks_added', 'Aufgabe angelegt'));
    },
    onError: (err) =>
      toast.error(
        tt('v2.detail.tasks_create_failed', 'Anlegen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const patchMut = useMutation({
    mutationFn: ({
      id,
      ...input
    }: { id: string; title?: string; status?: 'open' | 'done'; due_date?: string | null }) =>
      api.patchTask(id, input),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        tt('v2.detail.tasks_patch_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: () => {
      invalidate();
      toast.success(tt('v2.detail.tasks_deleted', 'Aufgabe gelöscht'));
    },
    onError: (err) =>
      toast.error(
        tt('v2.detail.tasks_delete_failed', 'Löschen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-3">
      {!isReader && (
        <TaskAddForm onAdd={(input) => createMut.mutate(input)} pending={createMut.isPending} />
      )}
      {isLoading && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {tt('v2.detail.loading', 'Lädt…')}
          </p>
        </Card>
      )}
      {!isLoading && items.length === 0 && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {tt('v2.detail.tasks_empty', 'Keine Aufgaben.')}
          </p>
        </Card>
      )}
      {!isLoading && items.length > 0 && (
        <Card padding={false}>
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {items.map((task) => (
              <TaskRow
                key={task.task_id}
                task={task}
                locale={i18n.language}
                isReader={isReader}
                onToggleDone={() =>
                  patchMut.mutate({
                    id: task.task_id,
                    status: task.status === 'done' ? 'open' : 'done',
                  })
                }
                onPatch={(input) => patchMut.mutate({ id: task.task_id, ...input })}
                onDelete={() => {
                  void confirmAction({
                    title: tt('v2.detail.tasks_delete_confirm', 'Aufgabe wirklich löschen?'),
                    tone: 'danger',
                    confirmLabel: tt('common.delete', 'Löschen'),
                  }).then((ok) => {
                    if (ok) deleteMut.mutate(task.task_id);
                  });
                }}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function TaskAddForm({
  onAdd,
  pending,
}: { onAdd: (input: { title: string; due_date: string | null }) => void; pending: boolean }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');

  const submit = () => {
    const titleClean = title.trim();
    if (!titleClean) return;
    onAdd({ title: titleClean, due_date: due ? new Date(due).toISOString() : null });
    setTitle('');
    setDue('');
  };

  return (
    <Card>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label
            className="block text-[10px] uppercase tracking-[0.1em] mb-1"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.detail.tasks_form_title', 'Aufgabe')}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={t('v2.detail.tasks_form_placeholder', 'Was ist zu tun?')}
            className="w-full text-sm px-3 py-2 rounded-md outline-none transition-colors"
            style={{
              background: 'var(--surface)',
              color: 'var(--ink)',
              border: '1px solid var(--line-soft)',
            }}
          />
        </div>
        <div>
          <label
            className="block text-[10px] uppercase tracking-[0.1em] mb-1"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.detail.tasks_form_due', 'Fällig')}
          </label>
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="text-sm px-3 py-2 rounded-md outline-none transition-colors"
            style={{
              background: 'var(--surface)',
              color: 'var(--ink)',
              border: '1px solid var(--line-soft)',
            }}
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!title.trim() || pending}
          className="text-xs px-3 py-2 rounded-md transition-colors"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: !title.trim() || pending ? 0.5 : 1,
            cursor: !title.trim() || pending ? 'not-allowed' : 'pointer',
          }}
        >
          {pending
            ? t('v2.detail.tasks_form_saving', 'Speichert…')
            : t('v2.detail.tasks_form_add', '+ Aufgabe')}
        </button>
      </div>
    </Card>
  );
}

function TaskRow({
  task,
  locale,
  isReader,
  onToggleDone,
  onPatch,
  onDelete,
}: {
  task: { task_id: string; title: string; status: string; due_date?: string | null | undefined };
  locale: string;
  isReader: boolean;
  onToggleDone: () => void;
  onPatch: (input: { title?: string; due_date?: string | null }) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(task.title);
  const [draftDue, setDraftDue] = useState(task.due_date ? task.due_date.slice(0, 10) : '');
  const overdue =
    task.due_date && task.status !== 'done' && new Date(task.due_date).getTime() < Date.now();
  const readerTooltip = isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined;

  if (editing) {
    return (
      <li className="p-3 flex items-center gap-2" style={{ borderColor: 'var(--line-soft)' }}>
        <input
          type="text"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          className="flex-1 text-sm px-2 py-1 rounded-md outline-none"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
          }}
        />
        <input
          type="date"
          value={draftDue}
          onChange={(e) => setDraftDue(e.target.value)}
          className="text-sm px-2 py-1 rounded-md outline-none"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
          }}
        />
        <button
          type="button"
          onClick={() => {
            const patch: { title?: string; due_date?: string | null } = {};
            if (draftTitle.trim() && draftTitle !== task.title) patch.title = draftTitle.trim();
            const newDue = draftDue ? new Date(draftDue).toISOString() : null;
            if (newDue !== (task.due_date ?? null)) patch.due_date = newDue;
            if (Object.keys(patch).length > 0) onPatch(patch);
            setEditing(false);
          }}
          className="text-xs px-2 py-1 rounded-md"
          style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none' }}
        >
          {t('common.save', 'Speichern')}
        </button>
        <button
          type="button"
          onClick={() => {
            setDraftTitle(task.title);
            setDraftDue(task.due_date ? task.due_date.slice(0, 10) : '');
            setEditing(false);
          }}
          className="text-xs px-2 py-1 rounded-md"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}
        >
          {t('v2.modal.cancel', 'Abbrechen')}
        </button>
      </li>
    );
  }

  return (
    <li className="p-3 flex items-center gap-3 group" style={{ borderColor: 'var(--line-soft)' }}>
      <button
        type="button"
        onClick={isReader ? undefined : onToggleDone}
        disabled={isReader}
        title={readerTooltip}
        className="w-4 h-4 rounded-sm flex items-center justify-center transition-colors shrink-0"
        style={{
          border: `1.5px solid ${task.status === 'done' ? 'var(--success)' : 'var(--ink-3)'}`,
          background: task.status === 'done' ? 'var(--success)' : 'transparent',
          cursor: isReader ? 'not-allowed' : 'pointer',
          opacity: isReader ? 0.6 : 1,
        }}
      >
        {task.status === 'done' && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
          >
            <path d="M5 12l5 5L20 7" />
          </svg>
        )}
      </button>
      <span
        className="flex-1 text-sm cursor-pointer"
        onClick={isReader ? undefined : () => setEditing(true)}
        style={{
          color: task.status === 'done' ? 'var(--ink-3)' : 'var(--ink)',
          textDecoration: task.status === 'done' ? 'line-through' : 'none',
          cursor: isReader ? 'default' : 'pointer',
        }}
        title={
          isReader ? readerTooltip : t('v2.detail.tasks_click_to_edit', 'Klick zum Bearbeiten')
        }
      >
        {task.title}
      </span>
      {task.due_date && (
        <span
          className="text-[11px] shrink-0"
          style={{ fontFamily: 'var(--f-mono)', color: overdue ? 'var(--danger)' : 'var(--ink-3)' }}
        >
          {new Intl.DateTimeFormat(locale).format(new Date(task.due_date))}
          {overdue ? ` · ${t('v2.detail.tasks_overdue', 'überfällig')}` : ''}
        </span>
      )}
      {!isReader && (
        <button
          type="button"
          onClick={onDelete}
          className="text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          style={{ color: 'var(--ink-3)' }}
          title={t('v2.detail.tasks_delete', 'Aufgabe löschen')}
        >
          ✕
        </button>
      )}
    </li>
  );
}

// ===========================================================================
// Tab: Staffing — Positionen und Besetzungen (#158/#163: `StaffingMaske`, auch in der
// Staffingseite und der Übergabe), Abschluss, Wiedervorlage, Kommentare.
// ===========================================================================

function StaffingTab({ project }: { project: Project }) {
  const { t, i18n } = useTranslation();
  const { canManage, canResolve } = useStaffingPermissions(project);
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  // Derselbe Abruf wie die Maske darunter (geteilter Query-Schlüssel) — für die Abschluss-Rückfrage.
  const maskeQuery = useQuery({
    queryKey: ['staffing-maske', project.project_id],
    queryFn: () => api.getStaffingMaske(project.project_id),
  });
  // Wie in „Staffing neu": offene Positionen + nicht zugeordnete Besetzungen + KI-Vorschläge
  // zum Bestätigen (#163, Spec E4) — sonst verschwänden unbestätigte Vorschläge still.
  const nochOffen = maskeQuery.data
    ? offenAnzahl(maskeQuery.data) +
      nichtZugeordnetAnzahl(maskeQuery.data) +
      maskeQuery.data.zu_bestaetigen
    : 0;

  const completedAt = project.staffing_completed_at ?? null;
  const completer = project.staffing_completed_by
    ? usersQuery.data?.items.find((u) => u.user_id === project.staffing_completed_by)
    : undefined;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
    void queryClient.invalidateQueries({ queryKey: ['v2-kl-staffing'] });
  };
  const completeMut = useMutation({
    mutationFn: () => api.completeStaffing(project.project_id),
    onSuccess: () => {
      invalidate();
      toast.success(t('v2.staffing.completed_ok', 'Staffing abgeschlossen'));
    },
    onError: (e) =>
      toast.error(
        t('v2.staffing.complete_failed', 'Abschluss fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });
  const reopenMut = useMutation({
    mutationFn: () => api.reopenStaffing(project.project_id),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['v2-phases'] });
      toast.success(t('v2.staffing.reopened_ok', 'Staffing wieder geöffnet'));
    },
    onError: (e) =>
      toast.error(
        t('v2.staffing.reopen_failed', 'Wieder öffnen fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });

  const onComplete = () => {
    void confirmAction({
      title: t('v2.staffing.complete_title', 'Staffing abschließen?'),
      body:
        nochOffen > 0
          ? t(
              'v2.staffing_maske.abschliessen_offen',
              'Es ist noch etwas offen, nicht zugeordnet oder zu bestätigen ({{n}}). Staffing trotzdem abschließen? Das Projekt verschwindet aus der Liste.',
              { n: nochOffen },
            )
          : t(
              'v2.staffing.complete_confirm',
              'Staffing für dieses Projekt abschließen? Das Projekt verschwindet aus der Staffing-Liste. Eine neue Position öffnet es automatisch wieder.',
            ),
      confirmLabel: t('v2.staffing.complete', 'Staffing abschließen'),
    }).then((ok) => {
      if (ok) completeMut.mutate();
    });
  };
  const onReopen = () => {
    void confirmAction({
      title: t('v2.staffing.reopen_title', 'Staffing wieder öffnen?'),
      body: t(
        'v2.staffing.reopen_confirm',
        'Staffing wieder öffnen? Der zugewiesene Projektleiter wird per Mail informiert. Die Phase bleibt unverändert.',
      ),
      confirmLabel: t('v2.staffing.reopen', 'Staffing wieder öffnen'),
    }).then((ok) => {
      if (ok) reopenMut.mutate();
    });
  };

  return (
    <div className="space-y-4">
      {/* „Staffing abgeschlossen"-Status (Staffing neu, Issue #1). */}
      {completedAt ? (
        <div
          className="flex items-center gap-3 flex-wrap rounded-md px-3 py-2"
          style={{ background: 'var(--success-soft)', border: '1px solid var(--success)' }}
        >
          <span className="text-sm font-medium" style={{ color: 'var(--success)' }}>
            ✓ {t('v2.staffing.completed_badge', 'Staffing abgeschlossen')}
          </span>
          <span
            className="text-[11px]"
            style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
          >
            {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
              new Date(completedAt),
            )}
            {completer ? ` · ${completer.name}` : ''}
          </span>
          {canResolve && (
            <button
              type="button"
              onClick={onReopen}
              disabled={reopenMut.isPending}
              className="ml-auto text-xs px-2.5 py-1 rounded"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--ink-2)',
                border: '1px solid var(--line-soft)',
                cursor: reopenMut.isPending ? 'not-allowed' : 'pointer',
                opacity: reopenMut.isPending ? 0.6 : 1,
              }}
            >
              ↺ {t('v2.staffing.reopen', 'Staffing wieder öffnen')}
            </button>
          )}
        </div>
      ) : (
        canResolve && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onComplete}
              disabled={completeMut.isPending}
              title={t(
                'v2.staffing.complete_hint',
                'Staffing für dieses Projekt abschließen — verschwindet aus der Liste',
              )}
              className="text-xs px-2.5 py-1 rounded"
              style={{
                background: 'var(--success-soft)',
                color: 'var(--success)',
                border: '1px solid var(--success)',
                cursor: completeMut.isPending ? 'not-allowed' : 'pointer',
                opacity: completeMut.isPending ? 0.6 : 1,
              }}
            >
              ✓ {t('v2.staffing.complete', 'Staffing abschließen')}
            </button>
          </div>
        )
      )}

      {/* „Wieder fällig am" (KL-Wiedervorlage) auch hier sicht-/setzbar (Staffing neu, Issue #1). */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.staffing.followup', 'Wieder fällig am')}
        </label>
        <KlFollowupDateEditor
          projectId={project.project_id}
          value={
            (project as unknown as { kl_followup_date?: string | null }).kl_followup_date ?? null
          }
          canEdit={canResolve || canManage}
        />
      </div>
      {/* #158: Übergabe-Notiz (= Intake „Sonstiges"), über der Maske. */}
      <UebergabeNotiz
        project={project}
        sonstiges={
          (project.intake_handover_fields as IntakeHandoverFields | null | undefined)
            ?.uebergabe_intake?.sonstiges
        }
      />
      {/* #158/#163: Positionen, Besetzungen, „Nicht zugeordnet" — lädt selbst. */}
      <StaffingMaske projectId={project.project_id} project={project} />
      {/* FA-4: Staffing-Kommentare auch hier im Staffing-Tab (Staffing neu, Issue #1). */}
      <div className="pt-3 border-t" style={{ borderColor: 'var(--line-soft)' }}>
        <h4
          className="text-[11px] uppercase tracking-[0.12em] mb-2"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.staffing.comments_title', 'Staffing-Kommentare')}
        </h4>
        <StaffingComments projectId={project.project_id} canComment={canManage || canResolve} />
      </div>
    </div>
  );
}

// ===========================================================================
// Tab: Comments / Briefings / Audit (Stubs mit Daten-Hinweis)
// ===========================================================================

// ===========================================================================
// Tab: Comments (Phase 5.7) — @mention-Editor + Liste
// ===========================================================================

function CommentsTab({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  // Kommentieren + Taggen darf jeder (auch Reader). Löschen nur eigene oder editor/admin.
  const { user } = useSession();
  // #123 (Review S2): Ausnahme zusammengeführtes Projekt — dort schreibt niemand mehr (Server 409).
  // Bewusst NICHT `useReaderMode()`: Leser dürfen sonst absichtlich kommentieren.
  const nurLesbar = useNurLesbar();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);

  const commentsQuery = useQuery({
    queryKey: ['v2-comments', projectId],
    queryFn: () => api.listStaffingComments(projectId),
  });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const usersById = useMemo(() => {
    const map = new Map<string, { user_id: string; name: string; email: string }>();
    for (const u of usersQuery.data?.items ?? []) map.set(u.user_id, u);
    return map;
  }, [usersQuery.data]);

  const add = useMutation({
    // Kommentare-Tab: neuer Kommentar ist ein allgemeiner Projekt-Kommentar (kein Staffing).
    mutationFn: () => api.createStaffingComment(projectId, { body, mentions, is_staffing: false }),
    onSuccess: () => {
      setBody('');
      setMentions([]);
      void queryClient.invalidateQueries({ queryKey: ['v2-comments', projectId] });
    },
    onError: (e) =>
      toast.error(
        t('v2.detail.comments_failed', 'Kommentar fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteStaffingComment(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['v2-comments', projectId] }),
  });

  const items = commentsQuery.data?.items ?? [];

  return (
    <div className="space-y-4">
      {!nurLesbar && (
        <Card>
          <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--ink)' }}>
            {t('v2.detail.comments_new', 'Neuer Kommentar')}
          </h3>
          <MentionInput
            value={body}
            entwurfSchluessel={entwurfSchluessel('projekt', projectId)}
            onChange={(b, m) => {
              setBody(b);
              setMentions(m);
            }}
            placeholder={t(
              'v2.detail.comments_placeholder',
              'Schreib was. @-Tippen erwähnt Personen.',
            )}
            rows={3}
          />
          <div className="mt-2 flex items-center justify-between">
            <span
              className="text-[11px]"
              style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
            >
              {mentions.length > 0
                ? t('v2.detail.comments_mentions_count', '{{count}} Erwähnung(en)', {
                    count: mentions.length,
                  })
                : t('v2.detail.comments_no_mentions', 'Keine Erwähnungen')}
            </span>
            <button
              type="button"
              onClick={() => add.mutate()}
              disabled={!body.trim() || add.isPending}
              className="text-xs px-3 py-1.5 rounded-md"
              style={{
                background: 'var(--primary)',
                color: 'var(--on-primary)',
                border: 'none',
                opacity: !body.trim() || add.isPending ? 0.5 : 1,
                cursor: !body.trim() || add.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {add.isPending
                ? t('v2.detail.comments_posting', 'Sendet…')
                : t('v2.detail.comments_post', 'Posten')}
            </button>
          </div>
        </Card>
      )}

      {commentsQuery.isLoading ? (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.loading', 'Lädt…')}
          </p>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.comments_empty', 'Noch keine Kommentare.')}
          </p>
        </Card>
      ) : (
        <Card padding={false}>
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {items.map((c) => {
              const author = c.created_by ? usersById.get(c.created_by) : undefined;
              const canDelete = !nurLesbar && (c.created_by === user?.user_id || !!user?.is_admin);
              return (
                <li
                  key={c.staffing_comment_id}
                  className="p-4"
                  style={{ borderColor: 'var(--line-soft)' }}
                >
                  <div
                    className="flex items-center justify-between mb-1.5 text-[11px]"
                    style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                  >
                    <span className="flex items-center gap-1.5">
                      {author?.name ?? t('v2.detail.audit_author_unknown', 'unbekannt')}
                      <HerkunftBadge
                        projectId={c.herkunft_project_id}
                        projectKey={c.herkunft_project_key}
                      />
                      {c.is_staffing && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide"
                          style={{
                            background: 'var(--surface-2)',
                            color: 'var(--ink-2)',
                            border: '1px solid var(--line-soft)',
                          }}
                        >
                          {t('v2.detail.comments_staffing_badge', 'Staffing')}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2">
                      <span>
                        {new Intl.DateTimeFormat('de', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        }).format(new Date(c.created_at))}
                      </span>
                      {/* #162: bearbeitet wird nur im Staffing-Block, angezeigt auch hier. */}
                      {c.edited_at && (
                        <span>
                          {t('v2.staffing.comment_edited', 'bearbeitet {{zeit}}', {
                            zeit: new Intl.DateTimeFormat(i18n.language, {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            }).format(new Date(c.edited_at)),
                          })}
                        </span>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => remove.mutate(c.staffing_comment_id)}
                          style={{ color: 'var(--danger)', cursor: 'pointer' }}
                          aria-label={t('v2.detail.comments_delete', 'Kommentar löschen')}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>
                    {renderMentionBody(c.body, usersById as never)}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ===========================================================================
// Tab: Mission Briefing (WP-11 MB-A) — Briefing + Anhänge + SF-Import
// ===========================================================================

// ---------------------------------------------------------------------------
// Briefing-Provenance-Hilfsfunktionen (MB-D)
// Lesen prompt_metadata DEFENSIV — alte Briefings ohne diese Felder crashen nicht.
// ---------------------------------------------------------------------------

/**
 * Extrahiert source aus prompt_metadata.
 * Gibt 'button' | 'auto' | undefined zurück (undefined = unbekannt / altes Briefing).
 */
function extractSource(
  meta: Record<string, unknown> | null | undefined,
): 'button' | 'auto' | undefined {
  if (!meta) return undefined;
  const raw = meta.source;
  if (raw === 'button' || raw === 'auto') return raw;
  return undefined;
}

/**
 * Gibt true zurück, wenn mcp_degraded === true ODER mcp_sources_used leer/fehlt.
 */
function isMcpDegraded(meta: Record<string, unknown> | null | undefined): boolean {
  if (!meta) return true;
  if (meta.mcp_degraded === true) return true;
  const sources = meta.mcp_sources_used;
  if (!Array.isArray(sources) || sources.length === 0) return true;
  return false;
}

/**
 * Gibt die mcp_sources_used-Liste zurück (nur string-Einträge, defensiv gefiltert).
 */
function extractSources(meta: Record<string, unknown> | null | undefined): string[] {
  if (!meta) return [];
  const raw = meta.mcp_sources_used;
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string');
}

/**
 * Rendert eine einzeilige, gedämpfte Provenance-Zeile für ein Briefing.
 */
function BriefingProvenance({
  meta,
  createdAt,
  locale,
}: {
  meta: Record<string, unknown> | null | undefined;
  createdAt: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const source = extractSource(meta);
  const degraded = isMcpDegraded(meta);
  const sources = extractSources(meta);

  const sourceLabel =
    source === 'button'
      ? t('v2.detail.briefings_prov_source_button', 'manuell erzeugt')
      : source === 'auto'
        ? t('v2.detail.briefings_prov_source_auto', 'automatisch (Neu→Setup)')
        : t('v2.detail.briefings_prov_source_unknown', 'Quelle unbekannt');

  const mcpLabel = degraded
    ? t('v2.detail.briefings_prov_no_mcp', 'ohne MCP-Quellen')
    : t('v2.detail.briefings_prov_sources', 'Quellen: {{list}}', { list: sources.join(', ') });

  const dateLabel = new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(createdAt));

  return (
    <div
      className="text-[10.5px] mt-2 flex flex-wrap gap-x-2 gap-y-0.5"
      style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-4)' }}
    >
      <span>{dateLabel}</span>
      <span aria-hidden="true">·</span>
      <span>{sourceLabel}</span>
      <span aria-hidden="true">·</span>
      <span>{mcpLabel}</span>
    </div>
  );
}

// Exportiert für den Komponententest `project-detail-briefing-button.test.tsx`
// (Issue #69). Der Rest der Seite bleibt intern — hier ist die VERDRAHTUNG des
// Wartezustands das Risiko, und die lässt sich nur an der echten Komponente prüfen.
export function BriefingsTab({ project }: { project: Project }) {
  const { t, i18n: i18nBrief } = useTranslation();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const [sfImportOpen, setSfImportOpen] = useState(false);

  const projectId = project.project_id;

  // Compute briefing_manage permission using the shared matrix.
  // useSession().user may be null on initial render — treat as no permission.
  // The local Project type uses segment: string; ProjectScopeShape requires Segment union.
  // We satisfy the shape explicitly to avoid the type widening issue.
  const canBriefingManage: boolean =
    user != null
      ? canDoProjectAction(
          user,
          {
            project_id: project.project_id,
            segment: project.segment as import('@projektverwaltung/shared').Segment,
            project_lead_user_id: project.project_lead_user_id ?? null,
            manager_user_id: project.manager_user_id ?? null,
            mtb_user_id: project.mtb_user_id ?? null,
            responsibility: project.responsibility ?? null,
            // #78: fuer briefing_manage bedeutungslos.
            phase_is_intake: project.phase_is_intake ?? false,
            lifecycle_status: project.lifecycle_status,
          },
          'briefing_manage',
        )
      : false;

  const briefingsQuery = useQuery({
    queryKey: ['v2-briefings', projectId],
    queryFn: () => api.listKiBriefings(projectId),
  });

  const invalidateBriefings = () => {
    void queryClient.invalidateQueries({ queryKey: ['v2-briefings', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['v2-briefings-all'] });
    // PV-052 §5: Ein Briefing kann `mtb_briefing_fehlt` erledigen — die API antwortet erst nach
    // der Neuprüfung, also zeigen Hinweisleiste und Marker nach diesem Neuladen den neuen Stand.
    void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
  };

  const generate = useMutation({
    mutationFn: () => api.generateKiBriefing(projectId),
    onSuccess: () => {
      toast.success(t('v2.detail.briefings_generated', 'Neues Briefing generiert'));
      invalidateBriefings();
    },
    // Fehlerfall selbstheilend halten (Issue #69): Läuft der Aufruf in eine
    // Zeitüberschreitung des vorgelagerten nginx (504), hat die API das Briefing
    // in aller Regel TROTZDEM fertiggestellt und gespeichert — der Vorfall vom
    // 2026-08-17 ist genau das (201 in der API, 504 im Browser). Deshalb hier
    // ebenfalls neu laden: Das erzeugte Briefing erscheint dann sofort in der
    // Liste, statt bis zum nächsten Seitenwechsel unsichtbar zu bleiben.
    //
    // Das ist bewusst unabhängig von der nginx-Zeitgrenze gebaut. Selbst wenn
    // die dort wieder zu knapp gerät, bleibt der sichtbare Schaden klein.
    onError: (e) => {
      invalidateBriefings();
      toast.error(
        t('v2.detail.briefings_generate_failed', 'Generierung fehlgeschlagen'),
        t(
          'v2.detail.briefings_generate_failed_hint',
          'Die Antwort kam nicht rechtzeitig zurück. Das Briefing wurde möglicherweise trotzdem erzeugt — die Liste wurde neu geladen.',
        ),
      );
      // Technische Ursache nicht im Toast, aber auffindbar halten.
      console.warn('briefing_generate_failed', e instanceof Error ? e.message : e);
    },
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => api.patchKiBriefing(id, { body }),
    onSuccess: () => {
      toast.success(t('v2.detail.briefings_saved', 'Briefing gespeichert'));
      invalidateBriefings();
    },
    onError: (e) =>
      toast.error(
        t('v2.detail.briefings_save_failed', 'Speichern fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });

  const items = (briefingsQuery.data?.items ?? [])
    .slice()
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const [latest, ...earlier] = items;

  const generateBtnLabel = generate.isPending
    ? t('v2.detail.briefings_generating', 'Generiert…')
    : latest
      ? t('v2.detail.briefings_regenerate', 'Neu generieren')
      : t('v2.detail.briefings_generate', 'Neues Briefing generieren');

  // Fehlklick-Schutz: Existiert bereits ein inhaltlich gefülltes Briefing, erst rückfragen.
  // Kein Datenverlust (Persistenz ist append-only) — das bisherige Briefing rutscht nur
  // in "Frühere Versionen". Der trim()-Guard ist defensiv: ein leerer body ist über
  // Zod (min(1)) + NOT NULL faktisch ausgeschlossen, reiner Whitespace theoretisch nicht.
  const handleGenerateClick = () => {
    const hasContent = Boolean(latest?.body?.trim());
    if (!hasContent) {
      generate.mutate();
      return;
    }
    void confirmAction({
      title: t('v2.detail.briefings_regenerate_confirm_title', 'Neues Briefing generieren?'),
      body: t(
        'v2.detail.briefings_regenerate_confirm',
        'Für dieses Projekt existiert bereits ein Briefing.\n\nSoll ein neues generiert werden? Das aktuelle Briefing wird ersetzt; die bisherige Fassung bleibt unter „Frühere Versionen" erhalten.',
      ),
      confirmLabel: t('v2.detail.briefings_regenerate', 'Neu generieren'),
    }).then((ok) => {
      if (ok) generate.mutate();
    });
  };

  return (
    <div className="space-y-5">
      {/* ——— MCP-Session-Warnung (nicht-blockierend, nur sichtbar wenn nicht connected) ——— */}
      <McpSessionWarning />

      {/* ——— Generate button row ——— */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {t('v2.detail.briefings_title', 'KI-Mission-Briefings')}
        </h3>
        <div className="flex items-center gap-2">
          {canBriefingManage && (
            <button
              type="button"
              onClick={() => setSfImportOpen(true)}
              className="text-xs px-3 py-1.5 rounded-md"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--ink)',
                border: '1px solid var(--line-soft)',
                cursor: 'pointer',
              }}
            >
              {t('v2.detail.briefings_sf_sync', 'SF-Dateien synchronisieren')}
            </button>
          )}
          <button
            type="button"
            onClick={handleGenerateClick}
            disabled={generate.isPending || !canBriefingManage}
            // Erwartungssteuerung VOR dem Klick (Issue #69): Die Generierung
            // dauert real 75–87 s. Der Hinweis muss deshalb schon anliegen,
            // solange der Button noch untätig ist — ein Tooltip, der erst
            // während des Laufs erscheint, kommt zu spät. Der Rechtehinweis hat
            // Vorrang: Wer nicht darf, braucht die Dauer nicht zu wissen.
            title={
              !canBriefingManage
                ? t('v2.reader.tooltip', 'Sie haben Leserechte')
                : t(
                    'v2.detail.briefings_generate_hint',
                    'kann bis zu 2 Minuten dauern aufgrund der Datenanalysen in Salesforce',
                  )
            }
            aria-busy={generate.isPending}
            className="text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
            style={{
              background: 'var(--primary)',
              color: 'var(--on-primary)',
              border: 'none',
              opacity: generate.isPending || !canBriefingManage ? 0.5 : 1,
              cursor: !canBriefingManage ? 'not-allowed' : generate.isPending ? 'wait' : 'pointer',
            }}
          >
            {generate.isPending && <Spinner size={11} />}
            {generateBtnLabel}
          </button>
        </div>
      </div>

      {/* ——— Laufender Hinweis (Issue #69) ———
          Der Spinner im Button zeigt „es passiert etwas", beantwortet aber nicht
          „darf ich hier weg?". Bei über einer Minute Wartezeit ist genau das die
          nächste Frage. `aria-live="polite"` meldet den Hinweis einmalig an
          Screenreader, ohne die laufende Ausgabe zu unterbrechen. */}
      {generate.isPending && (
        <Card>
          <p
            className="text-sm flex items-center gap-2"
            style={{ color: 'var(--ink-3)' }}
            aria-live="polite"
          >
            <Spinner size={13} />
            {t(
              'v2.detail.briefings_generating_hint',
              'Die Daten werden aus Salesforce zusammengetragen und ausgewertet. Das kann bis zu 2 Minuten dauern — die Seite kann offen bleiben.',
            )}
          </p>
        </Card>
      )}

      {/* ——— Loading / empty states ——— */}
      {briefingsQuery.isLoading && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.loading', 'Lädt…')}
          </p>
        </Card>
      )}

      {!briefingsQuery.isLoading && !latest && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.briefings_empty', 'Noch kein Briefing erzeugt.')}
          </p>
        </Card>
      )}

      {/* ——— Latest briefing ——— */}
      {latest && (
        <Card>
          <div
            className="flex items-center justify-between mb-3 text-[11px]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            <span className="flex items-center gap-1.5">
              <span style={{ color: 'var(--primary)' }}>
                {t('v2.detail.briefings_current', 'AKTUELL')}
              </span>
              <HerkunftBadge
                projectId={latest.herkunft_project_id}
                projectKey={latest.herkunft_project_key}
              />
            </span>
            <span>{latest.model ? `${latest.model}` : ''}</span>
          </div>
          <BriefingView
            briefingId={latest.ki_briefing_id}
            body={latest.body}
            canBriefingManage={canBriefingManage}
            isSaving={patchMut.isPending}
            onSave={(newBody) => patchMut.mutate({ id: latest.ki_briefing_id, body: newBody })}
            projectKey={project.project_key}
            customerName={project.customer_name}
          />
          <BriefingProvenance
            meta={latest.prompt_metadata ?? undefined}
            createdAt={latest.created_at}
            locale={i18nBrief.language}
          />
        </Card>
      )}

      {/* ——— Earlier versions (read-only, condensed) ——— */}
      {earlier.length > 0 && (
        <Card padding={false}>
          <div
            className="px-4 py-2 border-b text-[11px] uppercase tracking-[0.12em]"
            style={{
              borderColor: 'var(--line-soft)',
              fontFamily: 'var(--f-mono)',
              color: 'var(--ink-3)',
            }}
          >
            {t('v2.detail.briefings_earlier', 'Frühere Versionen ({{count}})', {
              count: earlier.length,
            })}
          </div>
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {earlier.map((b) => (
              <li
                key={b.ki_briefing_id}
                className="p-3"
                style={{ borderColor: 'var(--line-soft)' }}
              >
                <div
                  className="text-[11px] mb-1 flex items-center gap-1.5"
                  style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                >
                  {b.model ? `${b.model}` : ''}
                  <HerkunftBadge
                    projectId={b.herkunft_project_id}
                    projectKey={b.herkunft_project_key}
                  />
                </div>
                <div
                  className="text-xs whitespace-pre-wrap line-clamp-4"
                  style={{ color: 'var(--ink-2)' }}
                >
                  {b.body}
                </div>
                <BriefingProvenance
                  meta={b.prompt_metadata ?? undefined}
                  createdAt={b.created_at}
                  locale={i18nBrief.language}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ——— Attachments ——— */}
      <div className="pt-2 border-t" style={{ borderColor: 'var(--line-soft)' }}>
        <AttachmentList
          projectId={projectId}
          canBriefingManage={canBriefingManage}
          salesforceLightningBaseUrl={sfLightningBaseUrl(project.salesforce_url)}
        />
      </div>

      {/* ——— SF-Dateien Import Modal ——— */}
      <SfFileImportModal
        open={sfImportOpen}
        onClose={() => setSfImportOpen(false)}
        projectId={projectId}
      />
    </div>
  );
}

// ===========================================================================
// Tab: Audit (Phase 5.9) — project_history mit Event-Label-Mapping
// ===========================================================================

type AuditRow = {
  history_id: string;
  event_type: string;
  changed_at: string;
  changed_by?: string | null;
  note?: string | null;
  from_phase_id?: string | null;
  to_phase_id?: string | null;
};

const EVENT_LABEL_KEYS: Record<string, string> = {
  phase_change: 'v2.detail.audit_event_phase_change',
  lifecycle_completed: 'v2.detail.audit_event_lifecycle_completed',
  lifecycle_reactivated: 'v2.detail.audit_event_lifecycle_reactivated',
  projekt_zusammengefuehrt: 'v2.detail.audit_event_projekt_zusammengefuehrt',
  projekt_wiederhergestellt: 'v2.detail.audit_event_projekt_wiederhergestellt',
  blocked_flagged: 'v2.detail.audit_event_blocked_flagged',
  blocked_resolved: 'v2.detail.audit_event_blocked_resolved',
  qs_with_bl_flagged: 'v2.detail.audit_event_qs_with_bl_flagged',
  qs_with_bl_resolved: 'v2.detail.audit_event_qs_with_bl_resolved',
  staffing_request_created: 'v2.detail.audit_event_staffing_request_created',
  staffing_request_resolved: 'v2.detail.audit_event_staffing_request_resolved',
  responsibility_assigned: 'v2.detail.audit_event_responsibility_assigned',
  handover_field_completed: 'v2.detail.audit_event_handover_field_completed',
  auftrag_merged_into_project: 'v2.detail.audit_event_auftrag_merged_into_project',
  auftrag_split_from_project: 'v2.detail.audit_event_auftrag_split_from_project',
  sync_refresh: 'v2.detail.audit_event_sync_refresh',
  mention_mail_sent: 'v2.detail.audit_event_mention_mail_sent',
  project_fields_updated: 'v2.detail.audit_event_project_fields_updated',
  final_gesetzt: 'v2.detail.audit_event_final_gesetzt',
  final_entfernt: 'v2.detail.audit_event_final_entfernt',
  // #131
  auftrag_sf_geloescht: 'v2.detail.audit_event_auftrag_sf_geloescht',
  auftrag_sf_wiederhergestellt: 'v2.detail.audit_event_auftrag_sf_wiederhergestellt',
  sf_uebertragung_verworfen: 'v2.detail.audit_event_sf_uebertragung_verworfen',
  // #130: Sync-Historie und bisher fehlende Labels
  sf_importiert: 'v2.detail.audit_event_sf_importiert',
  sf_zuordnung_geaendert: 'v2.detail.audit_event_sf_zuordnung_geaendert',
  kfml_worklist_flagged: 'v2.detail.audit_event_kfml_worklist_flagged',
  kfml_worklist_resolved: 'v2.detail.audit_event_kfml_worklist_resolved',
  staffing_status_changed: 'v2.detail.audit_event_staffing_status_changed',
  notification_sent: 'v2.detail.audit_event_notification_sent',
  // #158: Staffing-Positionen und Besetzungen
  besetzung_angelegt: 'v2.detail.audit_event_besetzung_angelegt',
  besetzung_status_geaendert: 'v2.detail.audit_event_besetzung_status_geaendert',
  besetzung_zugeordnet: 'v2.detail.audit_event_besetzung_zugeordnet',
  besetzung_verknuepft: 'v2.detail.audit_event_besetzung_verknuepft',
  besetzung_geloescht: 'v2.detail.audit_event_besetzung_geloescht',
  bedarf_zugeordnet: 'v2.detail.audit_event_bedarf_zugeordnet',
};

const EVENT_TONE: Record<string, 'primary' | 'success' | 'warn' | 'danger' | 'neutral'> = {
  phase_change: 'primary',
  lifecycle_completed: 'success',
  lifecycle_reactivated: 'primary',
  projekt_zusammengefuehrt: 'neutral',
  projekt_wiederhergestellt: 'primary',
  blocked_flagged: 'danger',
  blocked_resolved: 'success',
  qs_with_bl_flagged: 'warn',
  qs_with_bl_resolved: 'success',
  // Feldaenderungen sind Alltag, kein Ereignis mit Signalfarbe.
  project_fields_updated: 'neutral',
  final_gesetzt: 'success',
  final_entfernt: 'neutral',
  // #131: Löschen in SF und verworfene Übertragung sind Signale, das Wiederauftauchen auch.
  auftrag_sf_geloescht: 'danger',
  auftrag_sf_wiederhergestellt: 'warn',
  sf_uebertragung_verworfen: 'warn',
  // #130: Eine Zuordnungsänderung aus SF ist ein Signal (sie kann einen Filter verlassen lassen).
  sf_importiert: 'neutral',
  sf_zuordnung_geaendert: 'warn',
  // #158: Löschen ist ein Signal, der Rest Alltag.
  besetzung_geloescht: 'warn',
  besetzung_angelegt: 'neutral',
  besetzung_status_geaendert: 'neutral',
  besetzung_zugeordnet: 'neutral',
  besetzung_verknuepft: 'neutral',
  bedarf_zugeordnet: 'neutral',
};

function AuditTab({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const historyQuery = useQuery({
    queryKey: ['v2-project-history', projectId],
    queryFn: () => api.projectHistory(projectId) as Promise<{ items: AuditRow[] }>,
  });
  const phasesQuery = useQuery({ queryKey: ['v2-phases'], queryFn: () => api.listPhases() });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });

  const phasesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of phasesQuery.data?.items ?? []) m.set(p.phase_id, p.name);
    return m;
  }, [phasesQuery.data]);
  const usersById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.user_id, u.name);
    return m;
  }, [usersQuery.data]);

  if (historyQuery.isLoading)
    return (
      <Card>
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.detail.loading', 'Lädt…')}
        </p>
      </Card>
    );
  const items = (historyQuery.data?.items ?? [])
    .slice()
    .sort((a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime());
  if (items.length === 0)
    return (
      <Card>
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.detail.audit_empty', 'Keine Audit-Einträge.')}
        </p>
      </Card>
    );

  return (
    <Card padding={false}>
      <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
        {items.map((row) => {
          const tone = EVENT_TONE[row.event_type] ?? 'neutral';
          const labelKey = EVENT_LABEL_KEYS[row.event_type];
          // #130: JSON-Notizen lesbar; der Wechsel des führenden Auftrags bringt sein Label mit.
          const notiz = verlaufNotiz(row.event_type, row.note, t as never);
          const label = notiz.label ?? (labelKey ? t(labelKey, row.event_type) : row.event_type);
          const author = row.changed_by
            ? (usersById.get(row.changed_by) ?? t('v2.detail.audit_author_unknown', 'unbekannt'))
            : t('v2.detail.audit_author_system', 'System');
          const from = row.from_phase_id ? phasesById.get(row.from_phase_id) : null;
          const to = row.to_phase_id ? phasesById.get(row.to_phase_id) : null;
          const showPhaseFlow = row.event_type === 'phase_change' && from && to && from !== to;
          return (
            <li
              key={row.history_id}
              className="p-3 flex items-start gap-3"
              style={{ borderColor: 'var(--line-soft)' }}
            >
              <span
                className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                style={{
                  background: tone === 'neutral' ? 'var(--surface-2)' : `var(--${tone}-soft)`,
                  color: tone === 'neutral' ? 'var(--ink-3)' : `var(--${tone})`,
                  fontFamily: 'var(--f-mono)',
                }}
              >
                {label}
              </span>
              <div className="flex-1 min-w-0">
                {showPhaseFlow && (
                  <div className="text-xs" style={{ color: 'var(--ink-2)' }}>
                    {from} → <strong>{to}</strong>
                  </div>
                )}
                {notiz.text && (
                  <div className="text-xs mt-0.5 italic" style={{ color: 'var(--ink-2)' }}>
                    "{notiz.text}"
                  </div>
                )}
              </div>
              <div
                className="text-[11px] text-right shrink-0"
                style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
              >
                <div>
                  {new Intl.DateTimeFormat(i18n.language, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(new Date(row.changed_at))}
                </div>
                <div>{author}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ===========================================================================
// Right Rail
// ===========================================================================

function RightRail({ project }: { project: Project }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const isReader = useReaderMode();
  const canEdit = !isReader && project.lifecycle_status === 'active';
  // #78: Zuteilung (PL/Manager) hat eine eigene Rechtefrage — in der Eingangsphase nur die
  // kaufmaennische Leitung. Der MTB bleibt davon unberuehrt.
  const canAssign = useCanAssignLeadManager({
    ...project,
    phase_is_intake: project.phase_is_intake ?? false,
  });
  const zuteilungGesperrt = canEdit && !canAssign;
  const zuteilungHinweis = t(
    'v2.detail.rail_assign_locked',
    'In Phase „Neu" nur durch die kaufmännische Leitung',
  );
  // #49: Der Einzel-Sync hängt an der Segment-Verantwortung, nicht am Reader-Flag — sonst
  // sieht jede Funktionsrolle einen aktiven Button und läuft in einen 403.
  const canSync = useCanSyncProject(project);

  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const users = usersQuery.data?.items ?? [];

  type PatchField = 'project_lead_user_id' | 'manager_user_id' | 'mtb_user_id';
  const patchMutation = useMutation({
    mutationFn: (input: { field: PatchField; value: string | null }) =>
      api.patchProject(project.project_id, { [input.field]: input.value }),
    onSuccess: () => {
      toast.success(t('v2.detail.assign_saved', 'Zuweisung gespeichert'));
      void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
    },
    onError: (e) =>
      toast.error(
        t('v2.detail.assign_failed', 'Zuweisung fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });

  const resyncMutation = useMutation({
    mutationFn: () => api.syncSingleProject(project.project_id),
    onSuccess: (result) => {
      // runSync schluckt SF-Errors und liefert 200 mit status='failure' zurück
      // (z.B. wenn SF-Creds fehlen). Reasonschnipsel aus errors[0].reason.
      if (result.status === 'failure') {
        const reason = result.errors[0]?.reason ?? 'unknown';
        toast.error(t('v2.detail.rail_resync_failed', 'Sync fehlgeschlagen'), reason);
        return;
      }
      const msg =
        result.status === 'partial'
          ? t('v2.detail.rail_resync_partial', 'Sync teilweise erfolgreich')
          : t('v2.detail.rail_resync_success', 'Aus Salesforce neu geladen');
      toast.success(msg);
      void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
      void queryClient.invalidateQueries({
        queryKey: ['v2-project-auftraege', project.project_id],
      });
      // PV-052 L2: Der Server hat abgewartet neu geprüft — Befunde frisch laden.
      void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
    },
    onError: (e) =>
      toast.error(
        t('v2.detail.rail_resync_failed', 'Sync fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });

  return (
    <aside className="space-y-4">
      <Card>
        <h3
          className="text-[11px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.detail.rail_responsible', 'Verantwortlich')}
        </h3>
        <AssignableRow
          label={t('v2.detail.rail_project_lead', 'Project Lead')}
          userId={project.project_lead_user_id}
          users={users}
          canEdit={canEdit && canAssign}
          disabledReason={zuteilungGesperrt ? zuteilungHinweis : undefined}
          required
          onAssign={(uid) => patchMutation.mutate({ field: 'project_lead_user_id', value: uid })}
        />
        <AssignableRow
          label={t('v2.detail.rail_manager', 'Manager')}
          userId={project.manager_user_id}
          users={users}
          canEdit={canEdit && canAssign}
          disabledReason={zuteilungGesperrt ? zuteilungHinweis : undefined}
          required
          onAssign={(uid) => patchMutation.mutate({ field: 'manager_user_id', value: uid })}
        />
        {/* MTB ist vom Zuteilungsschutz ausdruecklich NICHT betroffen (#78 §2.6). */}
        <AssignableRow
          label={t('v2.detail.rail_mtb', 'MTB')}
          userId={project.mtb_user_id}
          users={users}
          canEdit={canEdit}
          onAssign={(uid) => patchMutation.mutate({ field: 'mtb_user_id', value: uid })}
        />

        {zuteilungGesperrt && (
          <p className="mt-2 text-[11px] leading-snug" style={{ color: 'var(--ink-3)' }}>
            {zuteilungHinweis}
          </p>
        )}

        <SfWritePendingHinweis
          eintraege={project.sf_write_pending ?? []}
          canDiscard={canAssign}
          projectId={project.project_id}
        />
      </Card>

      <Card>
        <h3
          className="text-[11px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.tags.section', 'Tags')}
        </h3>
        <TagPicker projectId={project.project_id} segment={project.segment} canEdit={canEdit} />
      </Card>

      <Card>
        <h3
          className="text-[11px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.detail.rail_staffing', 'Staffing')}
        </h3>
        {project.staffing_needed ? (
          <div className="text-sm" style={{ color: 'var(--ink)' }}>
            <Badge tone="warn">{t('v2.detail.rail_staffing_open', 'offen')}</Badge>{' '}
            <span className="ml-1.5">
              {t('v2.detail.rail_staffing_flagged', 'Personalbedarf flagged')}
            </span>
          </div>
        ) : (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.rail_staffing_none', 'Kein offener Bedarf.')}
          </p>
        )}
      </Card>

      <Card>
        <h3
          className="text-[11px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.detail.rail_sync', 'Sync')}
        </h3>
        <div
          className="text-xs space-y-1.5"
          style={{ color: 'var(--ink-2)', fontFamily: 'var(--f-mono)' }}
        >
          <div>
            {t('v2.detail.rail_last_sync', 'Last sync:')}{' '}
            {project.last_sync_at
              ? new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }).format(new Date(project.last_sync_at))
              : '—'}
          </div>
          {project.salesforce_url && (
            <a
              href={project.salesforce_url}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--primary)' }}
            >
              {t('v2.detail.rail_sf_open', 'SF öffnen ↗')}
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={() => resyncMutation.mutate()}
          disabled={!canSync || resyncMutation.isPending}
          title={
            canSync
              ? undefined
              : t(
                  'v2.detail.rail_resync_no_permission',
                  'SF-Sync nur verfügbar, wenn Berechtigungen auf das Projekt-KuSe vorhanden sind',
                )
          }
          className="mt-3 text-xs px-2.5 py-1 rounded-md w-full"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
            opacity: !canSync || resyncMutation.isPending ? 0.5 : 1,
            cursor: canSync ? 'pointer' : 'not-allowed',
          }}
        >
          {resyncMutation.isPending
            ? t('v2.detail.rail_resync_pending', '↻ Lädt aus SF…')
            : t('v2.detail.rail_resync', '↻ Aus Salesforce neu laden')}
        </button>
      </Card>

      {/* Anhänge — kompakte, read-only Sicht (Verwaltung läuft im Briefing-Tab) */}
      <AttachmentList
        projectId={project.project_id}
        canBriefingManage={false}
        variant="compact"
        salesforceLightningBaseUrl={sfLightningBaseUrl(project.salesforce_url)}
      />
    </aside>
  );
}

// ===========================================================================
// Primitives
// ===========================================================================

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt
        className="text-[11px] uppercase tracking-[0.1em]"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {label}
      </dt>
      <dd style={{ color: 'var(--ink)' }}>{value}</dd>
    </>
  );
}

/**
 * #78 — Hinweis auf ausstehende Uebertragungen nach Salesforce.
 *
 * Zwei Stufen, bewusst unterschiedlich laut:
 *  - Unterhalb der Schwelle ein dezenter Hinweis („wird uebertragen") — **kein Fehlerton**.
 *    Eine kurze Salesforce-Sperre ist Alltag und soll niemanden beunruhigen.
 *  - Ab der Schwelle eine Warnung mit Zeitpunkt, Grund und dem Weg heraus. Ein haengender
 *    Abgleich darf nicht still im Protokoll verschwinden.
 *
 * Die Schwelle entscheidet das Backend (`visible`), damit Anzeige und Wiederholungslogik nicht
 * auseinanderlaufen.
 */
function SfWritePendingHinweis({
  eintraege,
  canDiscard,
  projectId,
}: {
  eintraege: NonNullable<Project['sf_write_pending']>;
  canDiscard: boolean;
  projectId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const verwerfen = useMutation({
    mutationFn: (topic: string) => api.discardSfWrite(projectId, topic),
    onSuccess: () => {
      toast.success(
        t(
          'v2.detail.sfwrite_discarded',
          'Übertragung verworfen — der Salesforce-Stand ist wiederhergestellt',
        ),
      );
      // Der Endpunkt zieht den Salesforce-Stand gleich nach; alle Sichten auf das Projekt
      // muessen deshalb neu laden, nicht nur die Detailabfrage.
      void qc.invalidateQueries({ queryKey: ['v2-project', projectId] });
      void qc.invalidateQueries({ queryKey: ['v2-project-auftraege', projectId] });
      void qc.invalidateQueries({ queryKey: ['v2-projects'] });
    },
    onError: (e) =>
      toast.error(
        t('v2.detail.sfwrite_discard_failed', 'Verwerfen fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });

  if (eintraege.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {eintraege.map((e) => {
        if (!e.visible) {
          return (
            <p key={e.topic} className="text-[11px] leading-snug" style={{ color: 'var(--ink-3)' }}>
              {t('v2.detail.sfwrite_pending', 'Wird nach Salesforce übertragen …')}
            </p>
          );
        }
        return (
          <div
            key={e.topic}
            className="rounded p-2 text-[11px] leading-snug"
            style={{ background: 'var(--warn-soft, var(--surface-3))', color: 'var(--ink-2)' }}
          >
            <p style={{ fontWeight: 500 }}>
              {t(
                'v2.detail.sfwrite_stuck',
                'Zuteilung konnte nicht nach Salesforce übertragen werden',
              )}
            </p>
            <p style={{ color: 'var(--ink-3)' }}>
              {t('v2.detail.sfwrite_since', 'Seit')} {new Date(e.since).toLocaleString()} ·{' '}
              {t('v2.detail.sfwrite_attempts', 'Versuche')}: {e.attempts}
            </p>
            {e.error_message ? (
              <p className="mt-1 break-words" style={{ color: 'var(--ink-3)' }}>
                {e.error_message}
              </p>
            ) : null}
            {canDiscard ? (
              <button
                type="button"
                className="mt-1.5 underline"
                style={{ color: 'var(--ink-2)' }}
                disabled={verwerfen.isPending}
                onClick={() => verwerfen.mutate(e.topic)}
              >
                {t('v2.detail.sfwrite_discard', 'Übertragung verwerfen')}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
